import { useSelector } from "react-redux";
import { student_routes } from "../../../router/all_routes";
import { useAcademicYear } from "../../../../core/common/context/AcademicYearContext";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  getStudentPaymentPlan,
  getStudentPayments,
} from "../../../../api/student";

const StudentPaymentList = () => {
  const { selectedYear } = useAcademicYear();

  //Initialisation des state utilisés
  const [allPayements, setAllPayements] = useState<any>({
    academicYear: null,
    student: { payments: [] },
  });
  const [allPayementPlans, setAllPayementPlans] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(true); // Pour le vrai chargement
  const [hasLoaded, setHasLoaded] = useState(false); // Pour éviter "Chargement..." quand il n'y a juste pas de données

  const [refreshKey, setRefreshKey] = useState(0);

  const fetchPayments = async () => {
    try {
      const response = await getStudentPayments(
        selectedYear?.Student?.id,
        selectedYear?.id,
      );
      const payments = response.data || [];

      setAllPayements(payments);

      // console.log("Payments", response)
    } catch (error: any) {
      // console.log("erreur de chargement des Paiements : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );

      // Option: reset les salles en cas d'erreur
      setAllPayements({ academicYear: null, student: { payments: [] } });
    } finally {
      setIsLoading(false); //Les données ne sont plus en chargement
      setHasLoaded(true); //Les données ont été chargé avec succès
    }
  };

  const fetchPaymentPlan = async () => {
    try {
      const response = await getStudentPaymentPlan(
        selectedYear?.Student?.id,
        selectedYear?.id,
      );
      const paymentPlan = response.data || [];

      setAllPayementPlans(paymentPlan);

      // console.log("Payment Plan", response)
    } catch (error: any) {
      // console.log("erreur de chargement des Plan de Paiements : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );

      // Option: reset les salles en cas d'erreur
      setAllPayementPlans([]);
    } finally {
      setIsLoading(false); //Les données ne sont plus en chargement
      setHasLoaded(true); //Les données ont été chargé avec succès
    }
  };

  useEffect(() => {
    // On attend que selectedYear soit défini
    if (!selectedYear?.id) return;
    fetchPaymentPlan();
    fetchPayments();
  }, [refreshKey, selectedYear]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
      .format(date)
      .replace(/^\w/, (c) => c.toUpperCase()); // Mettre la 1ère lettre en majuscule
  };

  const getDueStatus = (dueDateStr: string, paid: number, expected: number) => {
    const today = new Date();
    const due = new Date(dueDateStr);

    const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const d = new Date(due.getFullYear(), due.getMonth(), due.getDate());

    const fullyPaid = expected > 0 && paid >= expected;
    if (fullyPaid) {
      return {
        label: "Payé",
        badgeClass: "bg-success",
        progressClass: "bg-success",
        dueClass: "text-success",
      };
    }

    if (d < t) {
      return {
        label: "Échu",
        badgeClass: "bg-danger",
        progressClass: "bg-danger",
        dueClass: "text-danger",
      };
    }

    const sameMonth =
      d.getFullYear() === t.getFullYear() && d.getMonth() === t.getMonth();

    if (sameMonth) {
      return {
        label: "En cours",
        badgeClass: "bg-warning text-dark",
        progressClass: "bg-warning",
        dueClass: "text-muted",
      };
    }

    return {
      label: "À venir",
      badgeClass: "bg-info",
      progressClass: "bg-info",
      dueClass: "text-muted",
    };
  };

  const payments = allPayements?.student?.payments ?? [];

  const paidByPlanId = payments.reduce(
    (acc: Record<string, number>, payment: any) => {
      (payment?.repartitions ?? []).forEach((rep: any) => {
        const planId = rep?.payment_plan?.id;
        if (!planId) return;
        acc[planId] = (acc[planId] || 0) + Number(rep.amount || 0);
      });
      return acc;
    },
    {},
  );

  const totalDue = allPayementPlans.reduce(
    (sum: number, p: any) => sum + Number(p?.PaymentPlan?.amount || 0),
    0,
  );

  const totalPaid = allPayementPlans.reduce(
    (sum: number, p: any) => sum + Number(paidByPlanId[p?.id] || 0),
    0,
  );

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Mes Paiements</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">Frais de scolarité</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Paiements
                  </li>
                </ol>
              </nav>
            </div>
          </div>
          {/* /Page Header */}
          <div className="container">
            <div className="card mb-4">
              <div className="card-header pb-1 pt-2">
                <h5>
                  Montant total dû :{" "}
                  <strong>{totalDue.toLocaleString("fr-FR")} FCFA</strong>
                </h5>
              </div>
              <div className="card-body pb-1 pt-2">
                <h5>
                  Montant total payé :{" "}
                  <strong className="text-success">
                    {totalPaid.toLocaleString("fr-FR")} FCFA
                  </strong>
                </h5>
              </div>
            </div>

            {allPayementPlans.length === 0 && hasLoaded ? (
              <div className="col-12">
                <div className="card shadow-sm text-center p-5 border-0">
                  <div className="mb-3">
                    <i className="ti ti-inbox fs-1 text-muted" />
                  </div>
                  <h5 className="text-muted">
                    Aucun plan de paiement disponible
                  </h5>
                  <p className="text-muted mb-0">
                    Les plans de paiement qui vous sont attribués apparaîtront
                    ici.
                  </p>
                </div>
              </div>
            ) : (
              allPayementPlans.map((plan: any) => {
                const planId = plan.id;
                const expected = Number(plan.PaymentPlan.amount || 0);
                const paid = Number(paidByPlanId[planId] || 0);
                const percent =
                  expected > 0 ? Math.min((paid / expected) * 100, 100) : 0;
                // const remaining = Math.max(expected - paid, 0);
                const status = getDueStatus(
                  plan.PaymentPlan.due_date,
                  paid,
                  expected,
                );

                return (
                  <div className=" card mb-2">
                    <div className="card-header">
                      <div className="d-flex justify-content-between align-items-center">
                        <h6 className="mb-0">
                          {plan.PaymentPlan.label} - {plan.PaymentPlan.amount}{" "}
                          FCFA
                        </h6>

                        <span className={`badge ${status.badgeClass}`}>
                          {status.label}
                        </span>

                        {/* <span className="badge bg-success">En cours</span> */}
                      </div>
                    </div>
                    <div className="card-body">
                      <div className="">
                        <div className="d-flex justify-content-between">
                          <small>
                            Payé : {paid.toLocaleString("fr-FR")} FCFA /{" "}
                            {expected.toLocaleString("fr-FR")} FCFA
                          </small>
                        </div>
                        <div
                          className="progress mt-2 mb-2"
                          style={{ height: "8px" }}
                        >
                          <div
                            className={`progress-bar ${status.progressClass}`}
                            role="progressbar"
                            style={{ width: `${percent}%` }}
                            aria-valuenow={paid}
                            aria-valuemin={0}
                            aria-valuemax={expected}
                          ></div>
                        </div>
                        <div className="d-flex justify-content-end align-items-center w-100">
                          <small className={status.dueClass}>
                            Échéance : {formatDate(plan.PaymentPlan.due_date)}
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default StudentPaymentList;
